<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <h1 id="primary-heading" class="sr-only">Debug Console</h1>
            <div class="px-4 sm:px-6 lg:px-8">
                <div class="sm:flex sm:items-center">
                    <div class="sm:flex sm:space-x-4">
                        <h1 class="text-2xl font-medium text-taupe-800">
                            Debug Console
                        </h1>
                    </div>
                </div>

                <div class="mt-4 mt-2 flex space-x-8">
                    <div>
                        <zen-button @click="clearDebug" variant="secondary">
                            Clear
                        </zen-button>
                    </div>
                    <div class="align-middle p-2 flex space-x-3">
                        <button
                            type="button"
                            @click="toggleSubscribe"
                            :class="subscribed ? 'bg-sky-600' : 'bg-taupe-200'"
                            class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                            role="switch"
                            :aria-checked="subscribed"
                        >
                            <span class="sr-only">Use setting</span>
                            <span
                                aria-hidden="true"
                                :class="
                                    subscribed
                                        ? 'translate-x-5'
                                        : 'translate-x-0'
                                "
                                class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out"
                            ></span>
                        </button>
                        <span
                            v-if="subscribed"
                            class="text-taupe-500 uppercase"
                        >
                            Listening for requests
                        </span>
                        <span
                            v-if="!subscribed"
                            class="text-taupe-500 uppercase"
                        >
                            Not listening for requests
                        </span>
                    </div>
                </div>

                <zen-grid class="mt-8" :empty="results.length === 0">
                    <template #empty>
                        <zen-empty-table-message icon="ZenIconTerminal">
                            Waiting for requests...
                        </zen-empty-table-message>
                    </template>
                    <zen-grid-table>
                        <zen-grid-header>
                            <zen-grid-header-row>
                                <zen-grid-header-cell
                                    :hidable="false"
                                    title=""
                                    class="w-8"
                                />
                                <zen-grid-header-cell
                                    :hidable="false"
                                    title="Date/Time"
                                />
                                <zen-grid-header-cell title="Event Type" />
                                <zen-grid-header-cell title="Flag" />
                            </zen-grid-header-row>
                        </zen-grid-header>
                        <zen-grid-body>
                            <debug-console-row
                                v-for="result in results"
                                :key="result.requestId + '-' + result.dateTime"
                                :result="result"
                            />
                        </zen-grid-body>
                    </zen-grid-table>
                </zen-grid>
            </div>
        </section>
    </main>
</template>

<script>
import DebugConsoleRow from '@/screens/_components/project/DebugConsoleRow.vue'

import { useAccountEventsStore } from '@/stores/account-events'
import { useSessionStore } from '@/stores/session'

export default {
    name: 'ProjectDebugConsoleScreen',
    components: {
        DebugConsoleRow,
    },
    computed: {
        sessionStore() {
            return useSessionStore()
        },
    },
    data() {
        return {
            error: null,
            results: [],
            subscribed: false,
            unsubscribers: [],
            browserDebugEventName: 'zenmanage:debug-console-event',
        }
    },
    methods: {
        getEventNames() {
            return ['flag-evaluated', 'flag-json-requested', 'flag-used']
        },
        isCurrentScope(data) {
            const routeProject = this.$route.params.projectKey
            const routeEnvironment = this.$route.params.environmentKey

            if (data?.projectKey && data.projectKey !== routeProject) {
                return false
            }
            if (
                data?.environmentKey &&
                data.environmentKey !== routeEnvironment
            ) {
                return false
            }

            return true
        },
        fallbackFlagFromEvent(eventName) {
            if (eventName.startsWith('flag-json-requested')) {
                return {
                    key: '',
                    type: 'string',
                    value: {
                        string: 'requested',
                    },
                }
            }

            if (eventName.startsWith('flag-used')) {
                return {
                    key: '(flag-used)',
                    type: 'string',
                    value: {
                        string: 'used',
                    },
                }
            }

            return {
                key: '(unknown)',
                type: 'string',
                value: {
                    string: '(unknown)',
                },
            }
        },
        normalizeFlag(flag, eventName) {
            const fallback = this.fallbackFlagFromEvent(eventName)
            const normalizedFlag = { ...flag }
            normalizedFlag.key = normalizedFlag.key || fallback.key
            normalizedFlag.type = normalizedFlag.type || fallback.type

            if (normalizedFlag.type === 'boolean') {
                const value = normalizedFlag.value?.boolean
                normalizedFlag.value = {
                    boolean: typeof value === 'boolean' ? value : false,
                }
                return normalizedFlag
            }

            if (normalizedFlag.type === 'number') {
                const value = normalizedFlag.value?.number
                normalizedFlag.value = {
                    number: typeof value === 'number' ? value : 0,
                }
                return normalizedFlag
            }

            if (normalizedFlag.type === 'json') {
                // Keep the structured document as-is (object, array, or a
                // JSON scalar) rather than flattening it to a string. Only a
                // missing value falls back to null.
                const value = normalizedFlag.value?.json
                normalizedFlag.value = {
                    json: value === undefined ? null : value,
                }
                return normalizedFlag
            }

            normalizedFlag.type = 'string'
            const stringValue =
                normalizedFlag.value?.string ??
                (typeof normalizedFlag.value === 'string'
                    ? normalizedFlag.value
                    : null)

            normalizedFlag.value = {
                string: stringValue ?? fallback.value.string,
            }

            return normalizedFlag
        },
        withClientAgentAttribute(attributes, clientAgent) {
            if (!clientAgent) {
                return attributes
            }

            if (Array.isArray(attributes)) {
                return [
                    ...attributes.filter((attr) => attr.key !== 'Client Agent'),
                    { key: 'Client Agent', values: [{ value: clientAgent }] },
                ]
            }

            return { ...attributes, 'Client Agent': clientAgent }
        },
        normalizeResult(data, eventName) {
            const normalized = {
                ...data,
                requestId:
                    data?.requestId ||
                    data?.request_id ||
                    data?.requestID ||
                    'N/A',
                eventType:
                    data?.eventType ||
                    data?.event_type ||
                    data?.eventName ||
                    eventName ||
                    '(unknown)',
                dateTime: data?.dateTime || data?.date_time || new Date(),
                context: {
                    type: data?.context?.type || '(none)',
                    name: data?.context?.name || '(none)',
                    attributes: this.withClientAgentAttribute(
                        data?.context?.attributes || {},
                        data?.clientAgent
                    ),
                },
                flag: this.normalizeFlag(data?.flag, eventName),
            }

            return normalized
        },
        clearDebug() {
            this.results = []
        },
        toggleSubscribe() {
            if (this.subscribed) {
                this.unsubscribe()
            } else {
                this.subscribe()
            }
        },
        subscribe() {
            this.unsubscribe()

            const accountEvents = useAccountEventsStore()
            const eventNames = this.getEventNames()

            this.unsubscribers = eventNames.map((eventName) =>
                accountEvents.on(eventName, (data) => {
                    if (!this.isCurrentScope(data)) {
                        return
                    }

                    const normalizedResult = this.normalizeResult(
                        data,
                        eventName
                    )

                    this.results.unshift(normalizedResult)
                })
            )

            this.subscribed = eventNames.length > 0
        },
        unsubscribe() {
            this.unsubscribers.forEach((unsubscribe) => unsubscribe())
            this.unsubscribers = []
            this.subscribed = false
        },
        addBrowserDebugListener() {
            if (globalThis.window === undefined) {
                return
            }

            globalThis.window.addEventListener(
                this.browserDebugEventName,
                this.handleBrowserDebugEvent
            )
        },
        removeBrowserDebugListener() {
            if (globalThis.window === undefined) {
                return
            }

            globalThis.window.removeEventListener(
                this.browserDebugEventName,
                this.handleBrowserDebugEvent
            )
        },
        handleBrowserDebugEvent(event) {
            const eventName = event?.detail?.eventName

            if (typeof eventName !== 'string' || eventName === '') {
                return
            }

            // Test-only control event so e2e specs can drive the
            // listening state without a live realtime connection.
            if (eventName === 'set-subscribed') {
                this.subscribed = Boolean(event?.detail?.data?.subscribed)
                return
            }

            if (!this.subscribed) {
                return
            }

            const data = event?.detail?.data || {}

            if (!this.isCurrentScope(data)) {
                return
            }

            const normalizedResult = this.normalizeResult(data, eventName)

            this.results.unshift(normalizedResult)
        },
    },
    created() {
        this.addBrowserDebugListener()

        // Only listen when the account can have a live connection. Without it
        // the console stays idle, as it did before the shared connection.
        const key = import.meta.env.VITE_REVERB_APP_KEY
        const host = import.meta.env.VITE_REVERB_HOST
        const accountUlid = this.sessionStore.data?.account?.data?.ulid

        if (!key || !host || !accountUlid) {
            return
        }

        this.subscribe()
    },
    beforeUnmount() {
        this.removeBrowserDebugListener()
        this.unsubscribe()
    },
}
</script>

<style scoped>
:deep(.flow-root table) {
    table-layout: fixed;
    width: 100%;
}
</style>
